/* eslint-disable import/no-cycle */
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  Box,
  Button,
  ButtonCore,
  FooterActionBar,
  Icon,
  Tooltip,
} from '@saviynt/design-system';
import classname from 'classnames';
import PropTypes from 'prop-types';

import {
  SCREEN_SIZE_TABLET_SMALL,
  SCREEN_WIDTH_QUERIES,
  SYSTEM_DATA_THEMES,
} from '../../../utils/constant';
import { getMessageKey, handleRedirectOnClick } from '../../../utils/utils';
import {
  ACT_DISCONTINUE,
  ACT_EMAIL_HISTORY,
  ACT_EXPORT,
  ACT_NOTIFY,
  ACT_REASSIGN,
  ACTION_BUTTON_FOOTER_KIND,
  ACTION_BUTTONS_ICON,
  CERTS_LIST_ALL_TAB,
  CERTS_LIST_CANCELED_TAB,
  CERTS_LIST_DONE_TAB,
  CERTS_LIST_EXPIRED_TAB,
} from '../constant';

import CertsListActionMenu from './CertsListActionMenu';
import CertsListReassign from './CertsListReassign';

import './CertsListAction.css';

/**
 * CertsListAction Component
 *
 * This component renders an action button and a dropdown menu for a certification list.
 * It allows users to navigate to a specific certification management page or select actions
 * from a dynamically generated menu based on the provided `actionsMap`.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {string} props.kind - The kind of component required for tile/sidepanel.
 * @param {string} props.certKey - The key of the certification. Used to construct a URL for navigation.
 * @param {bool} props.isTabletWidth - If tablet width is applicable for viewport.
 * @param {bool} props.isMobileXlargeWidth - If mobile width is applicable for viewport.
 * @param {Array<string>} props.actionsMap - An array of action keys representing available actions for the dropdown menu.
 *
 * @returns {JSX.Element} The rendered CertsListAction component.
 *
 * @example
 * <CertsListAction
 *   kind
 *   certKey="12345"
 *   actionsMap={['edit', 'delete']}
 *   isTabletWidth={false},
 *   isMobileXlargeWidth={false}
 * />
 *
 * Behavior:
 * - The component maintains an internal state `optionsWithIcons` which stores an array of actions
 *   formatted with labels and icons. This state is updated whenever `actionsMap` changes.
 * - The main button navigates to a specific certification management page using the `certKey` in the URL.
 * - The dropdown menu is populated with actions based on the provided `actionsMap` and the predefined `ACTION_BUTTONS_MAP`.
 *
 * Key Features:
 * - **Dynamic Dropdown**: The menu options are dynamically generated based on `actionsMap`.
 * - **Navigation**: Clicking the main button redirects to a certification management page.
 * - **Icons**: Each menu option includes an icon for better visual context.
 *
 * Props:
 * - `kind` (string): A prop that will determin if componenet is required in tile or sidepanel
 * - `certKey` (string): A required prop that specifies the certification key.
 * - `actionsMap` (Array<string>): An optional array of action keys to determine the dropdown menu options.
 * - `isTabletWidth` (boolean): Whether tablet mode action buttons should be displayed.
 * - `isMobileXlargeWidth` (boolean): Whether mobile mode action buttons should be displayed.
 *
 * Default Props:
 * - `actionsMap` defaults to an empty array, resulting in no actions in the dropdown menu.
 *
 * Dependencies:
 * - **ACTION_BUTTONS_MAP**: An external object mapping action keys to their labels.
 * - **Logger**: Utility for logging events.
 * - **React.memo**: Used to optimize the rendering of the component by memoizing it.
 *
 * Example Output:
 * Given:
 * - `actionsMap = ['edit', 'delete']`
 * - `ACTION_BUTTONS_MAP = { edit: 'Edit', delete: 'Delete', view: 'View' }`
 *
 * The dropdown menu will display:
 * - Edit (with icon)
 * - Delete (with icon)
 */
function CertsListAction({
  kind,
  certKey,
  actionsMap,
  isPrimaryCertifierActive,
  tileCampaignKey,
  campaignType,
  tileStatusLabel,
  newCertUX,
  certDetailsUrl,
  handleTileClick,
  certificationName,
}) {
  const [optionsWithIcons, setOptionsWithIcons] = useState([]);
  const [isReassignOpen, setIsReassignOpen] = useState(false);
  const {
    core: { isAtleastOneTileActionable },
    global: { mediaQueryValue },
    tabs: { activeTab },
    sidePanel: { sidePanelMetaData },
  } = useSelector((state) => state?.certifications?.list);
  const intl = useIntl();
  const { messages } = useSelector((state) => state?.user) || {};

  // Responsive selector queries
  const tabletSmallQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_TABLET_SMALL
  );
  const isTabletWidth = tabletSmallQuery.value <= mediaQueryValue;

  const isReassignAPrimaryAction = Boolean(
    !isPrimaryCertifierActive &&
      optionsWithIcons?.some((action) => action?.value === ACT_REASSIGN)
  );

  const ACTION_BUTTONS_MAP = {
    [ACT_EXPORT]:
      messages?.CERTS_EXPORT || localize(intl, getMessageKey('CERTS_EXPORT')),
    [ACT_REASSIGN]:
      messages?.CERTS_REASSIGN ||
      localize(intl, getMessageKey('CERTS_REASSIGN')),
    [ACT_DISCONTINUE]:
      messages?.CERTS_CANCEL || localize(intl, getMessageKey('CERTS_CANCEL')),
    [ACT_EMAIL_HISTORY]:
      messages?.CERTS_LIST_ACTION_EMAIL_HISTORY_TITLE ||
      localize(intl, getMessageKey('CERTS_LIST_ACTION_EMAIL_HISTORY_TITLE')),
    [ACT_NOTIFY]:
      messages?.CERTS_LIST_NOTIFY ||
      localize(intl, getMessageKey('CERTS_LIST_NOTIFY')),
  };

  const ACTION_BUTTONS_MAP_INACTIVE = {
    [ACT_REASSIGN]:
      messages?.CERTS_REASSIGN ||
      localize(intl, getMessageKey('CERTS_REASSIGN')),
    [ACT_DISCONTINUE]:
      messages?.CERTS_CANCEL || localize(intl, getMessageKey('CERTS_CANCEL')),
    [ACT_EXPORT]:
      messages?.CERTS_EXPORT || localize(intl, getMessageKey('CERTS_EXPORT')),
    [ACT_EMAIL_HISTORY]:
      messages?.CERTS_LIST_ACTION_EMAIL_HISTORY_TITLE ||
      localize(intl, getMessageKey('CERTS_LIST_ACTION_EMAIL_HISTORY_TITLE')),
    [ACT_NOTIFY]:
      messages?.CERTS_LIST_NOTIFY ||
      localize(intl, getMessageKey('CERTS_LIST_NOTIFY')),
  };
  /**
   * Hook that runs a side effect to update options with icons
   * whenever the `actionsMap` dependency changes.
   *
   * This effect checks if `actionsMap` contains actions, then processes the
   * `ACTION_BUTTONS_MAP` object to create an ordered array of actions. Each
   * action is formatted as an object with `label`, `value`, and a predefined `icon`.
   * The resulting array is then stored using the `setOptionsWithIcons` function.
   *
   * Dependencies:
   * - `certKey` (String): Certification key/id to uniquely identify the record.
   * - `actionsMap` (Array): An array of action keys to be checked and processed.
   * - `isTabletWidth` (Boolean): Weather tablet width is available or not.
   * - `isMobileXlargeWidth` (Boolean): Mobile larege width is available or not.
   *
   * Assumptions:
   * - `ACTION_BUTTONS_MAP` is an object where keys represent action identifiers
   *   and values represent the associated button labels.
   * - `setOptionsWithIcons` is a function to update the state with the new array
   *   of actions.
   *
   * Behavior:
   * - If `actionsMap` is null or empty, no actions are processed.
   * - For each action present in `actionsMap`, the effect checks its existence
   *   in `ACTION_BUTTONS_MAP`.
   * - Only actions present in both `actionsMap` and `ACTION_BUTTONS_MAP` are
   *   included in the resulting array.
   *
   * Example Output:
   * Given:
   * - `actionsMap = ['edit', 'delete']`
   * - `ACTION_BUTTONS_MAP = { edit: 'Edit', delete: 'Delete', view: 'View' }`
   *
   * The resulting array passed to `setOptionsWithIcons` will be:
   * [
   *   { label: 'Edit', value: 'edit', icon: 'Sort' },
   *   { label: 'Delete', value: 'delete', icon: 'Sort' }
   * ]
   */

  useEffect(() => {
    if (actionsMap?.length > 0) {
      const actionsInOrder = [];

      Object.keys(
        isReassignAPrimaryAction
          ? ACTION_BUTTONS_MAP_INACTIVE
          : ACTION_BUTTONS_MAP
      ).forEach((action) => {
        if (actionsMap.includes(action)) {
          actionsInOrder.push({
            label: ACTION_BUTTONS_MAP[action],
            value: action,
            icon: ACTION_BUTTONS_ICON[action],
          });
        }
      });

      setOptionsWithIcons(actionsInOrder);
    }
  }, [actionsMap]);

  const getFooterText = (tab) => {
    const isView = Boolean(
      tab === CERTS_LIST_DONE_TAB ||
        tab === CERTS_LIST_CANCELED_TAB ||
        tab === CERTS_LIST_EXPIRED_TAB ||
        (tab === CERTS_LIST_ALL_TAB &&
          (sidePanelMetaData?.tab === CERTS_LIST_DONE_TAB ||
            sidePanelMetaData?.tab === CERTS_LIST_CANCELED_TAB ||
            sidePanelMetaData?.tab === CERTS_LIST_EXPIRED_TAB))
    );

    if (
      isReassignAPrimaryAction ||
      actionsMap?.length === 0 ||
      tileStatusLabel?.toLowerCase() === 'preview'
    ) {
      return (
        messages.CERTS_ACTION_VIEW ||
        localize(intl, getMessageKey('CERTS_ACTION_VIEW'))
      );
    }

    if (tileStatusLabel?.toLowerCase() === 'start certification') {
      return (
        messages.CERTS_ACTION_GET_STARTED ||
        localize(intl, getMessageKey('CERTS_ACTION_GET_STARTED'))
      );
    }

    if (isView) {
      return (
        messages.CERTS_ACTION_VIEW ||
        localize(intl, getMessageKey('CERTS_ACTION_VIEW'))
      );
    }

    return (
      messages.CERTS_ACTION_CONTINUE ||
      localize(intl, getMessageKey('CERTS_ACTION_CONTINUE'))
    );
  };

  if (kind === ACTION_BUTTON_FOOTER_KIND.footer) {
    return (
      <FooterActionBar className='CertsListAction-footerActionBar'>
        <div />
        <Box className='CertsListAction-sidepanelFooter'>
          <Button
            type='button'
            kind='filled'
            size='medium'
            rightIcon={
              <Icon kind='ArrowRight' size='medium' color='neutral-100' />
            }
            onClick={() => handleRedirectOnClick(newCertUX, certDetailsUrl)}>
            {getFooterText(activeTab)}
          </Button>
          {actionsMap?.length > 0 && (
            <CertsListActionMenu
              options={optionsWithIcons}
              certKey={certKey}
              isTabletWidth={isTabletWidth}
              kind={ACTION_BUTTON_FOOTER_KIND.footer}
              tileCampaignKey={tileCampaignKey}
              campaignType={campaignType}
              newCertUX={newCertUX}
              certDetailsUrl={certDetailsUrl}
              footerText={getFooterText(activeTab)}
              certificationName={certificationName}
            />
          )}
        </Box>
      </FooterActionBar>
    );
  }

  const listActionClasses = classname(
    'CertsListAction',
    isAtleastOneTileActionable &&
      actionsMap?.length === 0 &&
      'CertsListAction--noAction'
  );

  const primaryActionBtnClasses = classname(
    'CertsListActionButton',
    isAtleastOneTileActionable &&
      actionsMap?.length === 0 &&
      'CertsListActionButton--noAction',
    !isAtleastOneTileActionable &&
      actionsMap?.length === 0 &&
      'CertsListActionButton--isSingle'
  );

  return (
    <Box className={listActionClasses}>
      <Tooltip
        text={getFooterText(activeTab)}
        maxWidth='8rem'
        placement='bottom'
        offset={8}
        trigger={
          <ButtonCore
            id='Fixed ID here'
            className='CertsListAction-buttonCore'
            dataTheme={SYSTEM_DATA_THEMES.LIGHT}
            tabIndex={0}
            onClick={
              isReassignAPrimaryAction
                ? () => setIsReassignOpen(true)
                : () => handleRedirectOnClick(newCertUX, certDetailsUrl)
            }>
            <Box className={primaryActionBtnClasses}>
              <Icon
                className='CertsListActionButton-icon'
                kind={isReassignAPrimaryAction ? 'ReassignUser' : 'ArrowRight'}
              />
              <span className='CertsListActionButton-overlay' />
            </Box>
          </ButtonCore>
        }
      />
      {actionsMap?.length > 0 && (
        <>
          <CertsListActionMenu
            options={optionsWithIcons}
            certKey={certKey}
            isTabletWidth={isTabletWidth}
            kind={ACTION_BUTTON_FOOTER_KIND.tile}
            tileCampaignKey={tileCampaignKey}
            campaignType={campaignType}
            newCertUX={newCertUX}
            certDetailsUrl={certDetailsUrl}
            footerText={getFooterText(activeTab)}
            isReassignAPrimaryAction={isReassignAPrimaryAction}
            handleTileClick={handleTileClick}
            certificationName={certificationName}
          />
          <CertsListReassign
            isOpen={isReassignOpen}
            setIsOpen={setIsReassignOpen}
            certKey={certKey}
          />
        </>
      )}
    </Box>
  );
}

CertsListAction.propTypes = {
  kind: Object.values(ACTION_BUTTON_FOOTER_KIND),
  certKey: PropTypes.string.isRequired,
  actionsMap: PropTypes.arrayOf(PropTypes.string),
  isPrimaryCertifierActive: PropTypes.bool,
  tileCampaignKey: PropTypes.string.isRequired,
  campaignType: PropTypes.string.isRequired,
  tileStatusLabel: PropTypes.string.isRequired,
  newCertUX: PropTypes.string.isRequired,
  certDetailsUrl: PropTypes.string.isRequired,
  handleTileClick: PropTypes.func,
  certificationName: PropTypes.string.isRequired,
};

CertsListAction.defaultProps = {
  kind: ACTION_BUTTON_FOOTER_KIND.tile,
  actionsMap: [],
  isPrimaryCertifierActive: true,
  handleTileClick: () => {},
};

export default React.memo(CertsListAction);
